import ReactGA from 'react-ga4'
import md5 from 'md5'
import { isNullOrEmtpy } from '../Utils'
import { EventCategory, UserAction } from './analytics.constants'

type ConsentValue = 'granted' | 'denied'
type GaEventParam = string | number | boolean | null
type GaEventParams = Record<string, GaEventParam>

const GA_FIELD_MAX_LENGTH = 40

const toGaFieldName = (value: string, fallback: string): string => {
  const normalized = value
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
  const named = normalized || fallback
  const prefixed = /^[A-Za-z]/.test(named) ? named : `app_${named}`
  const fieldName = prefixed.slice(0, GA_FIELD_MAX_LENGTH).replace(/_+$/g, '')
  return fieldName || fallback
}

const toGaEventParam = (value: unknown): GaEventParam | undefined => {
  if (value === null) {
    return value
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return undefined
    }
  }
  return undefined
}

const toGaEventParams = (
  properties?: Record<string, unknown>,
): GaEventParams => {
  return Object.entries(properties ?? {}).reduce<GaEventParams>(
    (params, [key, value]) => {
      const gaValue = toGaEventParam(value)
      if (gaValue !== undefined) {
        params[toGaFieldName(key, 'property')] = gaValue
      }
      return params
    },
    {},
  )
}

export class GoogleAnalyticsService {
  private previousPageLocation = document.referrer
  private disabled = false

  constructor(
    private measurementId: string,
    private testMode: boolean = false,
    private debug: boolean = false,
  ) {}

  init(): void {
    this.disabled = false
    this.setGaDisabled(false)
    this.setConsent('default', 'granted')
    this.setConsent('update', 'granted')
    ReactGA.initialize(this.measurementId, {
      testMode: this.testMode,
      gaOptions: {
        cookieDomain: 'auto',
      },
      gtagOptions: {
        debug_mode: this.debug,
        send_page_view: false,
      },
    })
  }

  pageView(pathName: string): void {
    if (this.disabled) {
      return
    }
    const pageLocation = `${window.location.origin}${pathName}`
    ReactGA.gtag('event', 'page_view', {
      page_location: pageLocation,
      page_referrer: this.previousPageLocation,
      page_title: document.title,
    })
    this.previousPageLocation = pageLocation
  }

  event(category: EventCategory, action: UserAction, value?: string): void {
    if (this.disabled) {
      return
    }
    const eventAction = value ? `${action}:${value}` : action.toString()
    ReactGA.event('legacy_event', {
      event_action: eventAction,
      event_category: category,
    })
  }

  track(event: string, properties?: Record<string, unknown>): void {
    if (this.disabled) {
      return
    }
    ReactGA.event(toGaFieldName(event, 'app_event'), {
      ...toGaEventParams(properties),
      segment_event: event,
    })
  }

  updateUser(id?: string): void {
    if (this.disabled) {
      return
    }
    const userId = isNullOrEmtpy(id) ? '' : `${md5(id as string)}`
    ReactGA.set({ user_id: userId })
    if (!isNullOrEmtpy(id)) {
      ReactGA.event('login', { method: 'trustero' })
    }
  }

  disable(): void {
    this.disabled = true
    this.setGaDisabled(true)
    this.setConsent('update', 'denied')
    this.clearAnalyticsCookies()
  }

  private setConsent(command: 'default' | 'update', analytics: ConsentValue) {
    ReactGA.gtag('consent', command, {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: analytics,
    })
  }

  private setGaDisabled(disabled: boolean): void {
    const disableKey = `ga-disable-${this.measurementId}`
    const gaWindow = window as unknown as Record<string, boolean>
    gaWindow[disableKey] = disabled
  }

  private clearAnalyticsCookies(): void {
    const analyticsCookieNames = document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    const hostname = window.location.hostname
    const hostnameParts = hostname.split('.')
    const cookieDomains = new Set(['', hostname])

    if (hostnameParts.length > 1) {
      cookieDomains.add(`.${hostnameParts.slice(-2).join('.')}`)
    }
    if (hostnameParts.length > 2) {
      cookieDomains.add(`.${hostnameParts.slice(-3).join('.')}`)
    }

    analyticsCookieNames.forEach((name) => {
      cookieDomains.forEach((domain) => {
        document.cookie = [
          `${name}=`,
          'Max-Age=0',
          'expires=Thu, 01 Jan 1970 00:00:00 GMT',
          'path=/',
          domain ? `domain=${domain}` : '',
        ]
          .filter(Boolean)
          .join('; ')
      })
    })
  }
}
